import React from 'react';
import Icon from '../../../components/AppIcon';
import { format, parseISO } from 'date-fns';

const DetailedScoreBreakdown = ({ assessment, progressData }) => {
  if (!assessment) {
    return null;
  }

  const dimensions = [
    { key: 'data_infrastructure', label: 'Data Infrastructure', icon: 'Database', color: '#3B82F6' },
    { key: 'analytics_capabilities', label: 'Analytics Capabilities', icon: 'BarChart3', color: '#10B981' },
    { key: 'governance_maturity', label: 'Governance Maturity', icon: 'Shield', color: '#8B5CF6' },
    { key: 'team_skills', label: 'Team Skills', icon: 'Users', color: '#F59E0B' },
    { key: 'strategic_alignment', label: 'Strategic Alignment', icon: 'Target', color: '#EF4444' }
  ];

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Optimized';
    if (score >= 60) return 'Managed';
    if (score >= 40) return 'Defined';
    if (score >= 20) return 'Developing';
    return 'Initial';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-[#1FB5A5]';
    if (score >= 40) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#E6F0F5] rounded-lg flex items-center justify-center">
          <Icon name="FileText" size={20} className="text-[#002B45]" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#002B45]">Detailed Score Breakdown</h3>
          <p className="text-sm text-gray-500">{assessment?.title}</p>
        </div>
      </div>

      {/* Assessment Info */}
      <div className="bg-gradient-to-r from-[#1FB5A5]/10 to-[#002B45]/10 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-600 mb-1">Assessment Date</p>
            <p className="text-sm font-semibold text-[#002B45]">
              {format(parseISO(assessment?.createdAt), 'MMMM dd, yyyy')}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-1">Assessment Type</p>
            <p className="text-sm font-semibold text-[#002B45]">
              {assessment?.assessmentType === 'data_maturity' ? 'Data Maturity Assessment' : 'ROI Calculator'}
            </p>
          </div>
          {assessment?.results?.overallScore && (
            <div>
              <p className="text-xs text-gray-600 mb-1">Overall Score</p>
              <p className={`text-2xl font-bold ${getScoreColor(assessment?.results?.overallScore)}`}>
                {assessment?.results?.overallScore}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Dimension Scores */}
      {progressData?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-[#002B45] mb-4">Dimension Scores</h4>
          <div className="space-y-4">
            {dimensions?.map((dimension) => {
              const dimensionProgress = progressData?.find(p => p?.dimension === dimension?.key);

              if (!dimensionProgress) return null;

              return (
                <div key={dimension?.key} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${dimension?.color}20` }}
                      >
                        <Icon name={dimension?.icon} size={24} style={{ color: dimension?.color }} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#002B45]">{dimension?.label}</p>
                        <p className="text-xs text-gray-500">{getScoreLabel(dimensionProgress?.score)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-bold ${getScoreColor(dimensionProgress?.score)}`}>
                        {dimensionProgress?.score}
                      </p>
                      {dimensionProgress?.improvementPercentage > 0 && (
                        <p className="text-xs text-green-600 flex items-center gap-1 justify-end">
                          <Icon name="TrendingUp" size={12} />
                          +{dimensionProgress?.improvementPercentage?.toFixed(1)}%
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="h-3 rounded-full transition-all duration-500"
                        style={{
                          width: `${dimensionProgress?.score}%`,
                          backgroundColor: dimension?.color
                        }}
                      />
                    </div>
                  </div>

                  {/* Milestone */}
                  {dimensionProgress?.milestoneAchieved && (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-start gap-2">
                        <Icon name="Award" size={16} className="text-[#1FB5A5] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-[#002B45] mb-1">Milestone Achieved</p>
                          <p className="text-sm text-gray-700">{dimensionProgress?.milestoneAchieved}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {dimensionProgress?.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Notes:</p>
                      <p className="text-sm text-gray-600">{dimensionProgress?.notes}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Industry Benchmark Comparison */}
      {assessment?.results?.overallScore && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4">Industry Benchmark Comparison</h4>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-2">Your Score</p>
                <p className="text-3xl font-bold text-[#1FB5A5]">{assessment?.results?.overallScore}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-2">Industry Average</p>
                <p className="text-3xl font-bold text-gray-700">65</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-2">Top Performers</p>
                <p className="text-3xl font-bold text-gray-700">85</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  {assessment?.results?.overallScore >= 65
                    ? 'Your score is above the industry average. Continue building on your strengths to reach top performer status.'
                    : 'Focus on the recommended actions to improve your score and reach industry average performance.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedScoreBreakdown;